import React, { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { useParams } from "react-router";
import Badge from "../../components/ui/badge/Badge";
import ComponentCard from "../../components/common/ComponentCard";
import PageMeta from "../../components/common/PageMeta";
import useGoBack from "../../hooks/useGoBack";
import SubscriptionPlanService from "../../services/subscription-plan.service";
import { SubscriptionPlan } from "./types";

const formatBillingCycle = (interval?: string) => {
  if (interval === "year") return "Yearly";
  if (interval === "month") return "Monthly";
  return interval || "N/A";
};

const formatDate = (date?: string) => {
  if (!date) return "N/A";
  try {
    return format(parseISO(date), "MM/dd/yyyy hh:mm a");
  } catch {
    return date;
  }
};

const formatCurrency = (price?: number, currency?: string) => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency || "usd").toUpperCase(),
    }).format(price || 0);
  } catch {
    return `${price || 0} ${(currency || "").toUpperCase()}`;
  }
};

const DetailItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div>
    <p className="text-xs font-medium uppercase text-gray-400">{label}</p>
    <div className="mt-1 text-sm text-gray-800 dark:text-white/90">{value || "N/A"}</div>
  </div>
);

export default function ViewSubscriptionPlan() {
  const { plan_id } = useParams();
  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const goBack = useGoBack();

  useEffect(() => {
    const fetchPlan = async () => {
      if (!plan_id) return;
      try {
        setLoading(true);
        const response = await SubscriptionPlanService.getPlan(plan_id);
        setPlan(response.data?.data || response.data);
      } catch (err) {
        console.error("Error fetching subscription plan:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan_id]);

  return (
    <div>
      <PageMeta
        title="VIYUME - Subscription Plan Detail"
        description="Subscription Plan Detail"
      />
      <div className="mx-auto max-w-[80%] grid grid-cols-1 gap-6">
        <ComponentCard title="">
          {loading ? (
            <div className="space-y-4">
              {[...Array(6)].map((_, index) => (
                <div
                  key={index}
                  className="h-10 animate-pulse rounded bg-gray-200 dark:bg-gray-700"
                />
              ))}
            </div>
          ) : plan ? (
            <div className="space-y-8">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <h1 className="text-xl font-semibold text-gray-800 dark:text-white/90">
                    {plan.name}
                  </h1>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {plan.description || "No description"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge color={plan.isActive ? "success" : "error"}>
                    {plan.isActive ? "Active" : "Inactive"}
                  </Badge>
                  <button
                    onClick={() => {
                      window.location.href = `/edit-subscription-plan/${plan._id}`;
                    }}
                    className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                  >
                    Edit
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 rounded-lg border border-gray-200 p-5 dark:border-gray-800 md:grid-cols-3">
                <DetailItem label="Price" value={formatCurrency(plan.price, plan.currency)} />
                <DetailItem label="Billing Cycle" value={formatBillingCycle(plan.interval)} />
                <DetailItem label="Currency" value={(plan.currency || "N/A").toUpperCase()} />
              </div>

              <div className="grid grid-cols-1 gap-6 rounded-lg border border-gray-200 p-5 dark:border-gray-800 md:grid-cols-2">
                <DetailItem label="Apple Product ID" value={plan.appleProductId || "N/A"} />
                <DetailItem label="Google Product ID" value={plan.googleProductId || "N/A"} />
              </div>

              <div className="rounded-lg border border-gray-200 p-5 dark:border-gray-800">
                <p className="text-xs font-medium uppercase text-gray-400">Feature Limits</p>
                {plan.features && plan.features.length > 0 ? (
                  <ul className="mt-3 space-y-2">
                    {plan.features.map((feature) => (
                      <li
                        key={feature}
                        className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:bg-white/[0.03] dark:text-gray-300"
                      >
                        {feature}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">N/A</p>
                )}
              </div>

              <div className="grid grid-cols-1 gap-6 rounded-lg border border-gray-200 p-5 dark:border-gray-800 md:grid-cols-2">
                <DetailItem label="Created Date" value={formatDate(plan.createdAt)} />
                <DetailItem label="Updated Date" value={formatDate(plan.updatedAt)} />
              </div>

              <div className="flex justify-end">
                <button
                  onClick={goBack}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Back
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500">Subscription plan not found.</div>
          )}
        </ComponentCard>
      </div>
    </div>
  );
}
